// @flow
import Cookies from "js-cookie"
import keymirror from "keymirror"
import * as cookies from "@kiwicom/nitro/lib/services/session/cookies"
import { USER_ID } from "@kiwicom/nitro/lib/consts/cookies"

import jsonFetch from "common/services/fetch/jsonFetch"
import type Options from "common/records/Options"
import assembleApiUrl from "../../utils/assembleApiUrl"
import loadMissingFlightsPlacesNames from "./loadMissingFlightsPlacesNames"
import mapperCheckFlights from "./mappers/mapperCheckFlights"
import cuckoo from "common/services/cuckoo/cuckoo"
import { getApiEndpoints, cuckooEvents } from "common/consts/enums"
import { UA_SESSION_TOKEN } from "common/consts/auth"
import { fillPlacesToFlightObjMutable } from "common/services/journey/mapperJourney"
import type { LanguageInfo } from "common/records/LanguageInfo"
import type { Splitster } from "common/types/splitster"

export const errors = keymirror({
  INVALID: null,
})

type Props = {
  langInfo: LanguageInfo,
  // eslint-disable-next-line flowtype/no-weak-types
  params: Object,
  options: Options,
  affiliateId: string,
  retry?: number,
  loadMissingPlacesNames?: boolean,
  loadMissingPlacesNamesCacheOnly?: boolean,
  splitster?: Splitster,
}

const BASE_FETCH = [
  "grace_period_fee",
  "terminal",
  "vehicle",
  "fees_dropdown",
  "disabled_bags_in_mmb",
  "hidden_flights",
]

let inProgress = false
async function checkFlightsApi({
  langInfo,
  params,
  options,
  retry = 3,
  loadMissingPlacesNames = false,
  loadMissingPlacesNamesCacheOnly = false,
  splitster,
}: Props) {
  const fetch = BASE_FETCH.slice()
  const abVersions = options.get("simpleABVersions")

  if (abVersions.get("AB3") === "paymentCardWithFee") {
    fetch.push("payment_card_fees")
  }

  const payload = {
    ...params,
    bust: new Date().getTime(),
    user_token: Cookies.get(UA_SESSION_TOKEN),
    fetch: fetch.join("|"),
    // Deprecated
    bnum: 0,
    visitor_uniqid: cookies.load(USER_ID) || "",
  }

  if (options.get("apiKey")) {
    // Do not need affily for tequila
    delete payload.affily
  }

  cuckoo.track(cuckooEvents.BOOKING_CHECK_FLIGHTS_API_TRIGGERED)

  if (inProgress) {
    cuckoo.track(cuckooEvents.BOOKING_CHECK_FLIGHTS_API_SHOULD_CANCEL_PREVIOUS)
  }

  const url = assembleApiUrl(getApiEndpoints(options.branding).checkFlights, options.toJS())

  try {
    const json = await jsonFetch(
      url,
      {
        query: payload,
        retry,
        headers: {
          ...(options.get("apiKey") && { apikey: options.get("apiKey") }),
        },
      },
      { requestId: true },
    )
      // TODO: fix API
      // Coerce to JSON, API is sending Content-Type:text/html; charset=UTF-8
      .then(respData => (typeof respData === "string" ? JSON.parse(respData) : respData))

    inProgress = false
    if (json.status === "error") {
      return Promise.reject(json)
    }

    cuckoo.track(cuckooEvents.BOOKING_CHECK_FLIGHTS_API_SUCCESS, {
      requestId: json.headerRequestId,
      flightsChecked: json.flights_checked,
      flightsInvalid: json.flights_invalid,
      flightsRealChecked: json.flights_real_checked,
      flightsPrice: json.flights_price,
    })

    if (loadMissingPlacesNames) {
      try {
        const places = await loadMissingFlightsPlacesNames({
          url: options.locationsApiUrl,
          flights: json.flights,
          locale: langInfo.hreflang,
          cacheOnly: loadMissingPlacesNamesCacheOnly,
        })
        json.flights.forEach(flight => fillPlacesToFlightObjMutable(flight, places))
      } catch (err) {
        // Locations api call failed. Only track and use default flights from checkFlights
        cuckoo.track(cuckooEvents.BOOKING_LOADING_WITHOUT_LOCATIONS, err)
      }
    }

    return mapperCheckFlights(json, { splitster })
  } catch (err) {
    cuckoo.track(
      cuckooEvents.BOOKING_CHECK_FLIGHTS_API_ERROR,
      { err, url, payload },
      { disableLoggly: true },
    )
    inProgress = false
    return Promise.reject(err)
  }
}

export default checkFlightsApi
